import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface ModelPerformance {
  model: string
  accuracy: number
  precision: number
  recall: number
  f1: number
  drift_score: number
  status: string
}

interface DriftAlert {
  id: string
  model: string
  type: string
  severity: string
  feature: string
  detected: string
  status: string
}

interface Statistics {
  models_monitored: number
  drift_alerts_mtd: number
  performance_degraded: number
  avg_prediction_accuracy: number
  data_quality_score: number
  retraining_triggered: number
}

export default function ModelMonitoringPage() {
  const [performance, setPerformance] = useState<ModelPerformance[]>([])
  const [alerts, setAlerts] = useState<DriftAlert[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [perfRes, alertsRes, statsRes] = await Promise.all([
        fetch('/api/modelmonitoring/performance'),
        fetch('/api/modelmonitoring/drift-alerts'),
        fetch('/api/modelmonitoring/statistics')
      ])
      if (perfRes.ok) setPerformance(await perfRes.json())
      if (alertsRes.ok) setAlerts(await alertsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching model monitoring:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Healthy') return 'bg-green-100 text-green-800'
    if (status === 'Warning') return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  const getSeverityBadge = (severity: string) => {
    if (severity === 'High') return 'bg-red-100 text-red-800'
    if (severity === 'Medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-blue-100 text-blue-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Model Monitoring</h1>
          <p className="mt-2 text-gray-600">Phase 274 - Performance monitoring and drift detection</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Models Monitored</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.models_monitored}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Drift Alerts MTD</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.drift_alerts_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Degraded</p>
              <p className="text-2xl font-bold text-red-600">{statistics.performance_degraded}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Accuracy</p>
              <p className="text-2xl font-bold text-green-600">{statistics.avg_prediction_accuracy}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Data Quality</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.data_quality_score}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Retraining</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.retraining_triggered}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Drift Alerts</h2>
          </div>
          <div className="p-6 space-y-4">
            {alerts.map((alert) => (
              <div key={alert.id} className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{alert.model}</p>
                  <p className="text-sm text-gray-500">{alert.type} - {alert.feature}</p>
                  <p className="text-xs text-gray-400">{alert.detected}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${getSeverityBadge(alert.severity)}`}>{alert.severity}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${alert.status === 'Open' ? 'bg-red-100 text-red-800' : alert.status === 'Investigating' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{alert.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Model Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precision</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recall</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drift</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {performance.map((perf, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 font-medium text-gray-900">{perf.model}</td>
                    <td className="px-6 py-4 text-green-600">{perf.accuracy}%</td>
                    <td className="px-6 py-4 text-blue-600">{perf.precision}</td>
                    <td className="px-6 py-4 text-purple-600">{perf.recall}</td>
                    <td className="px-6 py-4">
                      <span className={perf.drift_score > 0.1 ? 'text-red-600' : 'text-green-600'}>{perf.drift_score}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(perf.status)}`}>{perf.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
